import { ref, reactive, watch, unref, type MaybeRef, type Component } from 'vue';
import type { MediaActionKey } from '@/composables/useMedia';
import {
	isMediaGallery,
} from '@/types/media';
import {
	type MediaFileType,
	type MediaConversionSize,
	type ApiV1,
} from '@teamworksdev/influencer/core';
export interface MediaComponent {
	component: Component;
	attrs: Record<string, unknown>;
}

export class MediaPreview implements ApiV1.Media {
	uuid: string;
	file_name: string;
	mime_type: string;
	file_size: number;
	file_type: MediaFileType;
	conversions: Partial<Record<MediaConversionSize, ApiV1.MediaConversion>>
		& { original: NonNullableFields<ApiV1.MediaConversion> };

	gallery?: Pick<ApiV1.Gallery, 'uuid' | 'title' | 'category'>;
	tags?: ApiV1.MediaTags;

	constructor(data: ApiV1.Media | ApiV1.MediaGallery) {
		this.uuid = data.uuid;
		this.file_name = data.file_name;
		this.mime_type = data.mime_type;
		this.file_size = data.file_size;
		this.file_type = data.file_type;
		this.conversions = data.conversions;
		this.tags = isMediaGallery(data) ? data.tags : undefined;
		this.gallery = isMediaGallery(data) ? data.gallery : undefined;
	}
}

interface UseMediaPreviewDialogState {
	isOpen: boolean;
	currentIndex: number;
	actionsMenuItems: MediaActionKey[] | undefined;
	download: boolean | undefined;
	enableIncrement: boolean | undefined;
	disableTagging: boolean | undefined;
	media: Array<MediaPreview | undefined> | undefined;
	onViewDetails: ((mediaUuid: string) => void) | undefined;
}

const DEFAULT_STATE: UseMediaPreviewDialogState = {
	isOpen: false,
	currentIndex: 0,
	actionsMenuItems: undefined,
	download: undefined,
	enableIncrement: undefined,
	disableTagging: undefined,
	media: undefined,
	onViewDetails: undefined,
} as const;

const getDefaultState = () => ({ ...DEFAULT_STATE });

const state = reactive<UseMediaPreviewDialogState>(getDefaultState());

function resetState() {
	Object.assign(state, getDefaultState());
}

const isRendered = ref<boolean>(false);

watch(() => state.isOpen, (isOpen) => {
	if (isOpen && !isRendered.value) isRendered.value = true;
	if (!isOpen) resetState();
});

interface UseMediaPreviewDialogOptions {
	actionsMenuItems?: MaybeRef<UseMediaPreviewDialogState['actionsMenuItems']>;
	download?: MaybeRef<UseMediaPreviewDialogState['download']>;
	enableIncrement?: MaybeRef<UseMediaPreviewDialogState['enableIncrement']>;
	disableTagging?: MaybeRef<UseMediaPreviewDialogState['disableTagging']>;
	onViewDetails?: MaybeRef<UseMediaPreviewDialogState['onViewDetails']>;
}

export function useMediaPreviewDialog(options?: UseMediaPreviewDialogOptions) {
	function open(media: Array<ApiV1.Media | ApiV1.MediaGallery | undefined>, index?: number) {
		state.actionsMenuItems = unref(options?.actionsMenuItems);
		state.download = unref(options?.download);
		state.enableIncrement = unref(options?.enableIncrement);
		state.disableTagging = unref(options?.disableTagging);
		state.onViewDetails = unref(options?.onViewDetails);

		state.media = media.map((m) => m ? new MediaPreview(m) : undefined);

		state.currentIndex = index ?? 0;

		state.isOpen = true;
	}

	return {
		state,
		isRendered,
		resetState,
		open,
	};
}
